import { useRef, useState } from "react";
import { AlertCircle, ArrowRightLeft, Loader2, Trash2 } from "lucide-react";

import { MoneyText } from "@workspace/accounting/components";
import type {
	OptionGroup,
	Product,
	ProductIngredient,
} from "@workspace/product-catalog-types";
import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemMedia,
	ItemTitle,
} from "@workspace/ui/components/item";
import { Label } from "@workspace/ui/components/label";
import {
	RadioGroup,
	RadioGroupItem,
} from "@workspace/ui/components/radio-group";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Textarea } from "@workspace/ui/components/textarea";
import { cn } from "@workspace/ui/lib/utils";

import type { EposCourse, OrderItem, SelectedOptionGroup } from "../../types";

interface OrderItemOptionsEditorProps {
	isOpen: boolean;
	/**
	 * HA-1025 / D-HA-72 — "add" is the options a product with a required group asks for when it is
	 * tapped, before any line exists: no Delete, no Move to course, and Save adds the line. The
	 * default is the editor of a line already on the order.
	 */
	mode?: "add" | "edit";
	onClose: () => void;
	orderItem: OrderItem;
	availableOptions: OptionGroup[];
	onSave: (
		itemId: string,
		selectedOptions: SelectedOptionGroup[],
		customNote: string,
		removedIngredients: ProductIngredient[],
	) => void | Promise<unknown>;
	/**
	 * HA-768 / D-HA-24 — the editor waits for these when they return a promise: it closes once the
	 * server has the change, and on a rejection stays open with the operator's input kept (the
	 * caller has already said what went wrong).
	 */
	onDelete?: (itemId: string) => void | Promise<unknown>;
	onMoveToCourse?: (itemId: string, toCourseId: string) => void;
	moveTargets?: EposCourse[];
	product: Product;
	/** HA-1025 / D-HA-74 (74-6) — which item of a long-press this popup is for, when there is more than one. */
	position?: { index: number; total: number };
	/**
	 * HA-1024 — why the server refused a send, a save or a payment for this line (a required group with no choice), in its
	 * own words. Shown inside the editor: as a toast it sat over Save Changes until it faded. It is shown even when the group
	 * has a default, as then no group is marked unanswered.
	 */
	refusal?: string;
}

export function OrderItemOptionsEditor({
	isOpen,
	mode = "edit",
	onClose,
	orderItem,
	availableOptions,
	onSave,
	onDelete,
	onMoveToCourse,
	moveTargets,
	product,
	position,
	refusal,
}: OrderItemOptionsEditorProps) {
	const [selectedOptions, setSelectedOptions] = useState<
		Map<string, Set<string>>
	>(() => {
		// Initialize from existing selections
		const map = new Map<string, Set<string>>();
		if (orderItem.selectedOptions) {
			for (const group of orderItem.selectedOptions) {
				const itemIds = new Set(group.selectedItems.map((item) => item.id));
				map.set(group.id, itemIds);
			}
		}

		const defs = availableOptions.filter((g) => g.isRequired);
		for (const group of defs) {
			if (!map.has(group.id) && group.items.length > 0) {
				const item = group.items.find((i) => i.isDefault);

				// HA-1025 — only a real default is pre-selected. The first option used to stand in
				// when there was none, which answered the group for the operator and hid that a
				// choice was missing.
				if (item) map.set(group.id, new Set([item.id]));
			}
		}

		return map;
	});

	const [customNote, setCustomNote] = useState(orderItem.notes || "");
	const [pending, setPending] = useState<"save" | "delete" | null>(null);
	// HA-1025 AC9 — set at once, where `pending` only shows on the next render: a second press
	// that lands before it does is still one save.
	const inFlight = useRef(false);

	const closeOnceDone = async (
		kind: "save" | "delete",
		result: void | Promise<unknown>,
	) => {
		setPending(kind);
		try {
			await result;
			onClose();
		} catch {
			// Stay open: the change didn't land, and the input is still here to try again.
		} finally {
			setPending(null);
			inFlight.current = false;
		}
	};

	const [freeTextOptions, setFreeTextOptions] = useState<Map<string, string>>(
		() => {
			// Initialize from existing selections
			const map = new Map<string, string>();
			if (orderItem.selectedOptions) {
				for (const group of orderItem.selectedOptions) {
					for (const item of group.selectedItems) {
						if (item.id.startsWith("custom-")) {
							map.set(group.id, item.name);
						}
					}
				}
			}
			return map;
		},
	);

	const [removedIngredients, setRemovedIngredients] = useState<
		Set<ProductIngredient>
	>(() => new Set());

	const handleSingleOptionChange = (groupId: string, itemId: string) => {
		setSelectedOptions((prev) => {
			const newMap = new Map(prev);
			newMap.set(groupId, new Set([itemId]));
			return newMap;
		});
	};

	const handleMultiOptionToggle = (groupId: string, itemId: string) => {
		setSelectedOptions((prev) => {
			const newMap = new Map(prev);
			const currentSet = newMap.get(groupId) || new Set();
			const newSet = new Set(currentSet);

			if (newSet.has(itemId)) {
				newSet.delete(itemId);
			} else {
				newSet.add(itemId);
			}

			if (newSet.size > 0) {
				newMap.set(groupId, newSet);
			} else {
				newMap.delete(groupId);
			}

			return newMap;
		});
	};

	const handleFreeTextChange = (groupId: string, value: string) => {
		setFreeTextOptions((prev) => {
			const newMap = new Map(prev);
			if (value.trim()) {
				newMap.set(groupId, value);
			} else {
				newMap.delete(groupId);
			}
			return newMap;
		});
	};

	const handleRemoveIngredientToggle = (item: ProductIngredient) => {
		setRemovedIngredients((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(item)) {
				newSet.delete(item);
			} else {
				newSet.add(item);
			}
			return newSet;
		});
	};

	// HA-1025 AC3 — a required group is answered by a pick or by its own text; until then Save is
	// off and the group says so.
	const isUnanswered = (group: OptionGroup) =>
		group.isRequired &&
		!selectedOptions.has(group.id) &&
		!freeTextOptions.has(group.id);
	const missingChoice = availableOptions.some(isUnanswered);

	const handleSave = () => {
		if (missingChoice || inFlight.current) {
			return;
		}
		inFlight.current = true;

		// Convert selections to SelectedOptionGroup format
		const selectedOptionGroups: SelectedOptionGroup[] = [];

		for (const group of availableOptions) {
			const selectedItemIds = selectedOptions.get(group.id);
			const freeText = freeTextOptions.get(group.id);

			if (selectedItemIds && selectedItemIds.size > 0) {
				const selectedItems = group.items
					.filter((item) => selectedItemIds.has(item.id))
					.map((item) => ({
						id: item.id,
						name: item.name,
						priceImpact: item.priceImpact,
					}));

				if (selectedItems.length > 0) {
					selectedOptionGroups.push({
						id: group.id,
						name: group.name,
						type: group.type,
						selectedItems,
					});
				}
			}

			// Add free text as a custom option item
			if (freeText) {
				const existingGroup = selectedOptionGroups.find(
					(g) => g.id === group.id,
				);
				const customItem = {
					id: `custom-${group.id}`,
					name: freeText,
					priceImpact: { value: 0, currency: "USD", version: 2 as const },
				};

				if (existingGroup) {
					existingGroup.selectedItems.push(customItem);
				} else {
					selectedOptionGroups.push({
						id: group.id,
						name: group.name,
						type: group.type,
						selectedItems: [customItem],
					});
				}
			}
		}

		void closeOnceDone(
			"save",
			onSave(
				orderItem.id,
				selectedOptionGroups,
				customNote,
				removedIngredients.size > 0 ? Array.from(removedIngredients) : [],
			),
		);
	};

	const handleDelete = () => {
		if (!onDelete || inFlight.current) return;
		inFlight.current = true;
		void closeOnceDone("delete", onDelete(orderItem.id));
	};

	const handleCancel = () => {
		onClose();
	};

	const availableIngredients =
		product.ingredients?.filter((x) => !x.required) || [];

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="!w-[95vw] !max-w-[95vw] h-[95vh] max-h-[95vh] flex flex-col p-0">
				<DialogHeader className="px-6 pt-6 pb-2 shrink-0">
					<DialogTitle>
						{mode === "add" ? "Options for" : "Edit Options for"}{" "}
						{orderItem.name}
						{position && (
							<span className="text-muted-foreground">
								{" "}
								({position.index} of {position.total})
							</span>
						)}
					</DialogTitle>
					<DialogDescription>
						Select options and add notes for this order item
					</DialogDescription>
				</DialogHeader>

				{refusal && (
					<Alert variant="destructive" className="mx-6 w-auto shrink-0">
						<AlertCircle />
						<AlertDescription>{refusal}</AlertDescription>
					</Alert>
				)}

				<ScrollArea className="flex-1 px-6 overflow-y-auto">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 py-4 w-full">
						{/* Left column: Options */}
						<div className="space-y-8">
							{availableOptions.map((group) => {
								const singleValue = selectedOptions
									.get(group.id)
									?.values()
									.next().value;
								return (
									<div key={group.id} className="space-y-4">
										<div className="flex items-center gap-2">
											<Label className="text-lg font-semibold">
												{group.name}
												{group.isRequired && (
													<span className="ml-1 text-destructive">*</span>
												)}
											</Label>
											<span className="text-sm text-muted-foreground">
												{group.type === "Single"
													? "(Select one)"
													: "(Select many)"}
											</span>
										</div>

										{isUnanswered(group) && (
											<p role="alert" className="text-sm text-destructive">
												Choose an option for {group.name}
											</p>
										)}

										{group.type === "Single" ? (
											<RadioGroup
												value={singleValue || ""}
												onValueChange={(value) =>
													handleSingleOptionChange(group.id, value)
												}
												className="space-y-2"
											>
												{group.items.map((item) => (
													<Item
														key={item.id}
														variant="outline"
														size="sm"
														className={cn(
															"aria-selected:border-primary hover:bg-accent/50 transition-colors cursor-pointer",
														)}
														aria-selected={singleValue === item.id}
														onClick={() =>
															handleSingleOptionChange(group.id, item.id)
														}
													>
														<ItemMedia variant="image">
															<RadioGroupItem
																value={item.id}
																id={item.id}
																className="scale-125"
															/>
														</ItemMedia>

														<ItemContent>
															<ItemTitle>{item.name}</ItemTitle>
															{item.description && (
																<ItemDescription>
																	{item.description}
																</ItemDescription>
															)}
														</ItemContent>
														<ItemActions>
															{item.priceImpact.value !== 0 && (
																<span className="ml-2 text-sm text-muted-foreground">
																	{item.priceImpact.value > 0 ? "+" : ""}
																	<MoneyText {...item.priceImpact} />
																</span>
															)}
														</ItemActions>
													</Item>
												))}
											</RadioGroup>
										) : (
											<div className="space-y-3">
												{group.items.map((item) => {
													const isChecked = selectedOptions
														.get(group.id)
														?.has(item.id);
													// HA-447 — the row is the checkbox's label, so a click anywhere on it
													// toggles once. It had its own onClick as well, which fired alongside
													// the checkbox's and undid it.
													return (
														<label
															key={item.id}
															className="flex items-center space-x-3 min-h-[48px] py-2 px-3 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
														>
															<Checkbox
																checked={isChecked}
																onCheckedChange={() =>
																	handleMultiOptionToggle(group.id, item.id)
																}
																className="scale-125"
															/>
															<span className="flex-1 cursor-pointer font-normal text-base leading-relaxed">
																{item.name}
																{item.priceImpact.value !== 0 && (
																	<span className="ml-2 text-sm text-muted-foreground">
																		{item.priceImpact.value > 0 ? "+" : ""}
																		{item.priceImpact.value}
																	</span>
																)}
															</span>
														</label>
													);
												})}
											</div>
										)}

										{/* Free text option for custom selections */}
										<div className="pt-2">
											<Input
												placeholder="Custom option (type your own)"
												value={freeTextOptions.get(group.id) || ""}
												onChange={(e) =>
													handleFreeTextChange(group.id, e.target.value)
												}
												className="w-full h-12 text-base"
											/>
										</div>
									</div>
								);
							})}
						</div>

						{/* Right column: Remove Ingredients & Notes */}
						<div className="space-y-8">
							{/* Remove Ingredients section */}
							{availableIngredients.length > 0 && (
								<div className="space-y-4 pb-6 border-b">
									<h3 className="text-xl font-semibold">Remove Ingredients</h3>
									<p className="text-base text-muted-foreground">
										Check any ingredients you want to remove from this item
									</p>
									<div className="space-y-3">
										{availableIngredients.map((item) => {
											const isRemoved = removedIngredients.has(item);

											return (
												// HA-447 — one toggle per click, as for Select-many above.
												<label
													key={`remove-${item.id}`}
													className="flex items-center space-x-3 min-h-[48px] py-2 px-3 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
												>
													<Checkbox
														checked={isRemoved}
														onCheckedChange={() =>
															handleRemoveIngredientToggle(item)
														}
														className="scale-125"
													/>
													<span className="flex-1 cursor-pointer font-normal text-base leading-relaxed">
														No {item.stockItemName}
													</span>
												</label>
											);
										})}
									</div>
								</div>
							)}

							{/* Per-item notes section */}
							<div className="space-y-4">
								<Label htmlFor="custom-note" className="text-lg font-semibold">
									Special Instructions
								</Label>
								<Textarea
									id="custom-note"
									placeholder="e.g., Extra sauce on the side, no ice..."
									value={customNote}
									onChange={(e) => setCustomNote(e.target.value)}
									className="min-h-[120px] text-base leading-relaxed"
								/>
							</div>
						</div>
					</div>
				</ScrollArea>

				<DialogFooter className="flex-col sm:flex-row gap-3 px-6 py-5 border-t shrink-0">
					<div className="flex gap-3 sm:mr-auto flex-col sm:flex-row">
						{mode === "edit" && onDelete && (
							<Button
								type="button"
								variant="destructive"
								onClick={handleDelete}
								disabled={pending !== null}
								className="h-12 text-base"
							>
								{pending === "delete" ? (
									<Loader2 className="mr-2 size-5 animate-spin" />
								) : (
									<Trash2 className="mr-2 size-5" />
								)}
								Delete Item
							</Button>
						)}
						{mode === "edit" &&
							moveTargets &&
							moveTargets.length > 0 &&
							onMoveToCourse && (
								<MoveToCourseButton
									itemId={orderItem.id}
									targets={moveTargets}
									onMove={(itemId, courseId) => {
										onMoveToCourse(itemId, courseId);
										onClose();
									}}
								/>
							)}
					</div>
					<div className="flex gap-3 flex-col sm:flex-row">
						<Button
							type="button"
							variant="outline"
							onClick={handleCancel}
							className="h-12 text-base"
						>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={handleSave}
							disabled={pending !== null || missingChoice}
							className="h-12 text-base"
						>
							{pending === "save" && (
								<Loader2 className="mr-2 size-5 animate-spin" />
							)}
							{mode === "add" ? "Save" : "Save Changes"}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function MoveToCourseButton({
	itemId,
	targets,
	onMove,
}: {
	itemId: string;
	targets: EposCourse[];
	onMove: (itemId: string, courseId: string) => void;
}) {
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	// AC11: single target → move directly
	if (targets.length === 1) {
		const target = targets[0]!;
		return (
			<Button
				type="button"
				variant="outline"
				onClick={() => onMove(itemId, target.id)}
				className="h-12 text-base"
			>
				<ArrowRightLeft className="mr-2 size-5" />
				Move to {target.name}
			</Button>
		);
	}

	// Multiple targets → show picker
	return (
		<>
			<Button
				type="button"
				variant="outline"
				onClick={() => setIsPickerOpen(true)}
				className="h-12 text-base"
			>
				<ArrowRightLeft className="mr-2 size-5" />
				Move to Course
			</Button>
			<Dialog open={isPickerOpen} onOpenChange={setIsPickerOpen}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Move to Course</DialogTitle>
						<DialogDescription>
							Select the course to move this item to.
						</DialogDescription>
					</DialogHeader>
					<div className="flex flex-col gap-2">
						{targets.map((course) => (
							<Button
								key={course.id}
								variant="outline"
								className="h-12 text-base justify-start"
								onClick={() => {
									onMove(itemId, course.id);
									setIsPickerOpen(false);
								}}
							>
								{course.name}
							</Button>
						))}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
